import { useState } from 'react';
import { Input, SvgIcon, Toggle, TooltipComponent, theme } from '@salecycle/ui-component-library';
import { useSetControlGroup } from '../state/actions/useSetControlGroup';
import { useDisplayWebContent } from '../hooks/useDisplayWebContent';
import { ControlCard } from 'sections/onsite-campaigns/pages/CampaignEditor/components/ControlCard';
import styled from 'styled-components';
import { useIsSplitTestControlGroupSelector } from '../../../../state/SplitTests/selectors/useIsSplitTestControlGroupSelector';
import { useSetControlGroupSplitTest } from '../../../../state/SplitTests/actions/useSetControlGroupSplitTest';
import { useTranslation } from 'react-i18next';
import { useWorkflowForm } from '../../../../contexts/CampaignWorkflowFormContext';

const ControlWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing3};
`;

const ControlGroupHeader = styled.div`
  display: flex;
  align-items: center;
  > div:first-child {
    background: ${({ theme }) => theme.designToken.neutral50};
    border-radius: 50%;
    display: flex;
    height: 40px;
    padding: ${({ theme }) => theme.designToken.spacing1} ${({ theme }) => theme.designToken.spacing2};
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 40px;
    > svg {
      margin-top: 5px;
    }
  }
`;

const ControlGroupTitle = styled.div`
  font: ${({ theme }) => theme.designToken.headingH3};
  margin-left: ${({ theme }) => theme.designToken.spacing3};
`;

const ControlGroupDescription = styled.div`
  font: ${({ theme }) => theme.designToken.body2};
  margin-top: ${({ theme }) => theme.designToken.spacing2};
`;

const ToggleContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.designToken.spacing1};
  #controlGroupTooltip > div + div {
    position: fixed;
    top: 108px;
    left: 215px;
  }
`;

const ControlGroupPercentage = styled.div`
  margin-top: ${({ theme }) => theme.designToken.spacing3};
  display: flex;
  justify-content: end;
  padding-right: 25px;
  > div > div {
    margin: 0;
  }
  > div > div > div > div:last-child {
    position: absolute;
    right: -24px;
    top: 33%;
  }
`;

const ControlGroup = ({ messageId, isSplitTest }) => {
  const { t } = useTranslation('campaigns');
  const { formDisabled } = useWorkflowForm();

  const { getControlGroup } = useDisplayWebContent();
  const splitTestControlGroup = useIsSplitTestControlGroupSelector(messageId);
  const messageControlGroup = getControlGroup.controlGroupEnabled(messageId);
  const controlGroupWeight = getControlGroup.controlGroupWeight(messageId);

  const controlGroup = () => {
    if (isSplitTest) {
      return splitTestControlGroup;
    } else {
      return messageControlGroup;
    }
  };

  const [controlGroupEnabled, setControlGroupEnabled] = useState(controlGroup);
  const setControlGroupSplitTest = useSetControlGroupSplitTest();
  const setControlGroupMessages = useSetControlGroup();

  const setControlGroup = isSplitTest ? setControlGroupSplitTest : setControlGroupMessages;

  // set control group weight state first
  const [weight, setWeight] = useState(controlGroupWeight);

  const handleToggleChange = () => {
    setControlGroupEnabled(!controlGroupEnabled);
    // set control group with the weight and not the default 10
    setControlGroup(messageId, !controlGroupEnabled, weight);
  };

  const handleNumberChange = (value: string) => {
    let parsedWeight = parseInt(value, 10);
    // Allow empty values to stop users not typing the values
    // they want, before the value would be stuck on 9
    // and could not be deleted. Now it resets to 0 allowing every number
    if (value === '') {
      parsedWeight = 0;
    }

    // don't let strings in, keep the value the same
    if (isNaN(parsedWeight)) return;

    if (parsedWeight < 0) {
      parsedWeight = 0;
    } else if (parsedWeight > 100) {
      parsedWeight = 100;
    }

    setWeight(parsedWeight);
    // leave the controlGroupEnabled as it was
    setControlGroup(messageId, controlGroupEnabled, parsedWeight);
  };

  return (
    <ControlWrapper>
      <ToggleContainer>
        <Toggle
          ariaLabel={
            isSplitTest
              ? t('onsite.editor.workflow.splitTest.drawer.toggleAriaLabel')
              : t('onsite.editor.workflow.displayWebContentMessage.drawer.toggleAriaLabel')
          }
          label={t('onsite.editor.workflow.splitTest.drawer.toggleLabel')}
          disabled={formDisabled}
          checked={controlGroupEnabled}
          onChange={handleToggleChange}
          testId={'controlGroupToggle'}
          margin={false}
        />
        <TooltipComponent
          id="controlGroupTooltip"
          label={t('onsite.editor.workflow.splitTest.contolGroupCard.tooltipLable')}
          position="Right"
        >
          <SvgIcon name="circle-question" size="16" color={theme.designToken.primary700} />
        </TooltipComponent>
      </ToggleContainer>

      {controlGroupEnabled && (
        <ControlCard aria-label={t('onsite.editor.workflow.splitTest.contolGroupCard.controlCardAriaLabel')}>
          <ControlGroupHeader>
            <SvgIcon name="user-slash" size="24" />
            <ControlGroupTitle aria-label="Control group">
              {t('onsite.editor.workflow.splitTest.contolGroupCard.title')}
            </ControlGroupTitle>
          </ControlGroupHeader>
          <ControlGroupDescription>
            {t('onsite.editor.workflow.splitTest.contolGroupCard.description')}
          </ControlGroupDescription>
          <ControlGroupPercentage>
            <Input
              onChange={handleNumberChange}
              ariaLabel={t('onsite.editor.workflow.splitTest.contolGroupCard.controlCardInputAriaLabel')}
              maxWidth="70px"
              numberInputUnit="%"
              placeholder="Placeholder"
              type="number"
              value={weight}
            />
          </ControlGroupPercentage>
        </ControlCard>
      )}
    </ControlWrapper>
  );
};

export default ControlGroup;
